import { getAxiosClient } from './api';
import { getMetadata } from '~loader/loader-utils';
import editorConfig from '~config/editorConfig';
import { EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER } from '~config/editorConstants';
import { getState, modifyState } from '~src/state';
import { isLoggedUserGlobalEditor } from '~src/user';

const createTemplateAndPagesRelation = async (templateId, pagesIds) => {
  const httpClient = await getAxiosClient();

  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  const url = `${process.env.PARSE_API_URL}/${tableName}/${templateId}`;
  const method = 'PUT';
  const data = {};

  data.relations = [
    {
      class: `${tableName}_pages`,
      column: `${tableName}_pages`,
      toIds: pagesIds,
    },
  ];

  return httpClient({ url, method, data: { data }, responseType: 'json' });
};

async function saveTemplatePages(templateId, firstSave = false) {
  const httpClient = await getAxiosClient();
  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');

  let url;
  let method;

  if (firstSave === false) {
    const currentPageIndex = getState('currentPageIndex');

    try {
      // salvo la pagina attuale
      const savedPage = JSON.stringify(
        window.canvas.toJSON(editorConfig.additionalJsonProperties)
      );
      modifyState(`pages[${currentPageIndex}].canvasJson`, savedPage);
    } catch (error) {
      modifyState(`pages[${currentPageIndex}].canvasJson`, '');
    }
  }

  const pagesIds = [];
  // let savedPagesCount = 0;
  const savePagePromises = [];
  const pages = getState('pages');

  const isGlobalEditor = await isLoggedUserGlobalEditor();

  for (let page of pages) {
    const data = {};

    data.canvasJson = page.canvasJson;
    data.order = page.order;

    if (page.animations) {
      if (typeof page.animations === 'string') {
        data.animations = page.animations;
      } else {
        data.animations = JSON.stringify(page.animations);
      }
    }

    if (isGlobalEditor) {
      data.isGlobal = true;
    }

    const editorMode = getState('editorMode');

    if (editorMode === EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER) {
      data.editor_templates_default_pointer = templateId;
    } else {
      data.editor_templates_pointer = templateId;
    }

    if (format) {
      data.editor_project_formats_pointer = format.objectId;
    }

    if (formatDefault) {
      data.editor_project_formats_default_pointer = formatDefault.objectId;
    }

    let tableName;

    if (editorMode === EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER) {
      tableName = 'editor_templates_default_pages';
    } else {
      tableName = 'editor_templates_pages';
    }

    if (page.objectId) {
      data.objectId = page.objectId;
      url = `${process.env.PARSE_API_URL}/${tableName}/${page.objectId}`;
      method = 'PUT';
    } else {
      url = `${process.env.PARSE_API_URL}/${tableName}`;
      method = 'POST';
    }

    const promise = httpClient({
      url,
      method,
      data: { data },
      responseType: 'json',
    });

    savePagePromises.push(promise);
  }

  const responses = await Promise.all(savePagePromises);

  responses.forEach((response, index) => {
    const pageId = response.data.data.objectId;
    modifyState(`pages[${index}].objectId`, pageId);
    pagesIds.push(pageId);
  });

  return pagesIds;
}

export const saveEditorTemplate = async (firstSave = false) => {
  const httpClient = await getAxiosClient();

  const metadata = getMetadata();

  const data = {
    metadata,
  };

  const projectType = getState('selected.tipo');
  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');

  if (projectType.isGlobal) {
    data.editor_project_types_default_pointer = projectType.objectId;
  } else {
    data.editor_project_types_pointer = projectType.objectId;
  }

  // Se c'e' un formato custom salvo il pointer
  if (format) {
    data.editor_project_formats_pointer = format.objectId;
  }

  if (formatDefault) {
    data.editor_project_formats_default_pointer = formatDefault.objectId;
  }

  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  let url;
  let method;

  if (firstSave) {
    url = `${process.env.PARSE_API_URL}/${tableName}`;
    method = 'POST';
  } else {
    url = `${process.env.PARSE_API_URL}/${tableName}/${window.editorLoader.templateId}`;
    method = 'PUT';
  }

  const response = await httpClient({
    url,
    method,
    data: { data },
    responseType: 'json',
  });
  const templateId = response.data.data.objectId;

  // I have saved the canvas, now I will save all the pages
  const pagesIds = await saveTemplatePages(templateId, firstSave);

  // Now that I have saved each page and the canvas, I create a relation from
  // the canvas and the pages
  await createTemplateAndPagesRelation(templateId, pagesIds);
};

export const fetchTemplateById = async (id) => {
  const httpClient = await getAxiosClient(true);

  const pointersToInclude = [
    'editor_project_formats_default_pointer',
    'editor_project_formats_pointer',
    'editor_project_templates_default_pointer',
    'editor_project_templates_pointer',
    'editor_project_types_default_pointer',
    'editor_project_types_pointer',
    'editor_templates_categories_default_pointer',
    'editor_templates_categories_pointer',
  ];

  const payload = {};
  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    payload.isGlobal = true;
    tableName = 'editor_templates_default';
  } else {
    tableName = 'editor_templates';
  }

  const response = await httpClient.get(
    `${process.env.PARSE_API_URL}/${tableName}`,
    {
      params: {
        filterEqual_objectId: id,
        include: pointersToInclude.join(','),
      },
    }
  );

  if (!response.data.data[0]) {
    const error = new Error('Template not found');
    return error;
  }
  const obj = response.data.data[0];

  if (!obj) {
    // not found
    const error = new Error('Template not found');
    return error;
  }

  modifyState('selected.template', obj);

  // Tipo - Default
  if (obj.editor_project_types_default_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_default_pointer);
  }

  // Tipo - Custom
  if (obj.editor_project_types_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_pointer);
  }

  // Formato - Default
  if (obj.editor_project_formats_default_pointer) {
    modifyState(
      'selected.formatoDefault',
      obj.editor_project_formats_default_pointer
    );
  }

  // Formato - Custom
  if (obj.editor_project_formats_pointer) {
    modifyState('selected.formato', obj.editor_project_formats_pointer);
  }

  // Categoria Modello - Default
  if (obj.editor_templates_categories_default_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_default_pointer
    );
  }

  // Categoria Modello - Custom
  if (obj.editor_templates_categories_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_pointer
    );
  }

  return obj;
};
